import React, { useState } from "react";
import SidebarAdmin from "./SidebarAdmin";
import AdminNavbar from "./AdminNavbar";
import PostAdminButton from "../Assets/PostAdminButton";
import ModalConfirmation from "../Assets/ModalConfirmation";
import AdminSearchBar from "../Assets/AdminSearchBar";
import NewsModal from "./NewsModal";
import useNews from "../Hooks/useNews";
import { NewsItem } from "../Types/Types";
import { useTranslation } from "react-i18next";

interface AdminNewsProps {
  lang: string;
}

const AdminNews: React.FC<AdminNewsProps> = ({ lang }) => {
  const { t } = useTranslation();
  const { news, loading, error, searchQuery, setSearchQuery, createNews, updateNews, deleteNews } = useNews();

  const [showForm, setShowForm] = useState(false);
  const [itemToEdit, setItemToEdit] = useState<NewsItem | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const handleDeleteConfirm = async () => {
    if (selectedId) await deleteNews(selectedId);
    setSelectedId(null);
    setModalOpen(false);
  };

  return (
    <div className="min-h-screen bg-gray-100 flex font-montserrat">
      <div className="w-64">
        <SidebarAdmin />
      </div>

      <div className="flex-1 flex flex-col">
        <AdminNavbar />
        <div className="flex-1 py-12 px-8 lg:px-24 space-y-10">
          <h1 className="text-4xl text-brand-800">{t("navbar.news")}</h1>

          <div className="flex justify-between items-center space-x-4">
            <PostAdminButton onClick={() => { setItemToEdit(null); setShowForm(true); }} />
            <AdminSearchBar searchQuery={searchQuery} onSearch={setSearchQuery} />
          </div>

          {loading ? (
            <p className="text-gray-500">Loading…</p>
          ) : error ? (
            <p className="text-red-500">{error}</p>
          ) : (
            <div className="bg-white rounded-xl shadow overflow-hidden">
              <table className="w-full text-left">
                <thead className="bg-gray-50 text-sm text-gray-600">
                  <tr>
                    <th className="p-4">{t("tableHeaders.image")}</th>
                    <th className="p-4">{t("forms.title")}</th>
                    <th className="p-4">{t("tableHeaders.createdAt")}</th>
                    <th className="p-4 text-right">{t("tableHeaders.action")}</th>
                  </tr>
                </thead>
                <tbody>
                  {news.map((item) => (
                    <tr key={item._id} className="border-t border-gray-100">
                      <td className="p-4">
                        {item.image ? (
                          <img src={item.image} alt="" className="h-12 w-16 rounded object-cover" />
                        ) : (
                          <span className="text-gray-300">—</span>
                        )}
                      </td>
                      <td className="p-4 font-medium text-gray-800">{item.title?.[lang] || item.title?.en}</td>
                      <td className="p-4 text-sm text-gray-500">
                        {item.createdAt ? new Date(item.createdAt).toLocaleDateString() : ""}
                      </td>
                      <td className="p-4 text-right space-x-3">
                        <button
                          onClick={() => { setItemToEdit(item); setShowForm(true); }}
                          className="text-brand-600 hover:underline"
                        >
                          ✎
                        </button>
                        <button
                          onClick={() => { setSelectedId(item._id); setModalOpen(true); }}
                          className="text-red-500 hover:underline"
                        >
                          🗑
                        </button>
                      </td>
                    </tr>
                  ))}
                  {news.length === 0 && (
                    <tr>
                      <td colSpan={4} className="p-6 text-center text-gray-500">{t("news.empty")}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {showForm && (
        <NewsModal
          isOpen={showForm}
          initialData={itemToEdit || undefined}
          onClose={() => { setShowForm(false); setItemToEdit(null); }}
          onSubmit={async (formData) => {
            if (itemToEdit) await updateNews(itemToEdit._id, formData);
            else await createNews(formData);
            setShowForm(false);
            setItemToEdit(null);
          }}
        />
      )}

      <ModalConfirmation
        isOpen={modalOpen}
        onClose={() => { setSelectedId(null); setModalOpen(false); }}
        onConfirm={handleDeleteConfirm}
      />
    </div>
  );
};

export default AdminNews;
